<template>
  <div
    :style="[styleObj]"
    class="img-wrap"
    @click="emit('click', $event)"
    ref="target"
    :id="photo.slug"
  >
    <img
      v-if="isPhoto && isVisibleOnce"
      :src="imageUrl"
      :title="label"
      :alt="label"
      loading="lazy"
      :width="photo.width"
      :height="photo.height"
    />
    <video
      v-else-if="showVideo"
      ref="video"
      playsinline
      muted
      loop
      :width="photo.width"
      :height="photo.height"
    >
      <source :src="videoUrl" :type="photo.mimetype" />
    </video>
    <!-- <div v-else :style="placeholderStyle"></div> -->
  </div>
</template>

<script lang="ts" setup>
import type { Photo } from "~/api";
import { useWindowSize } from "@vueuse/core";

const emit = defineEmits(["click"]);

const target: Ref<HTMLDivElement | undefined> = ref();
const video: Ref<HTMLVideoElement | null> = ref(null);

const isVisible = useElementVisibility(target);
const isVisibleOnce = useElementVisibilityOnce(target);

const props = defineProps({
  photo: {
    type: Object as PropType<Photo>,
    required: true,
  },

  dialog: {
    type: Boolean as PropType<boolean>,
    default: false,
  },

  basis: {
    type: Number as PropType<number>,
    default: 350,
  },
});

const { photo, dialog } = toRefs(props);

// const playVideoPromise = ref(undefined as Promise<void> | undefined);
// const canRemove = ref(true);
// const holdVideo = ref(false);

// large values don't work well on mobile

const { width } = useWindowSize();
const basis = computed(() => Math.min(width.value, props.basis));
const imageWidth = computed(() => photo.value.width);
const imageHeight = computed(() => photo.value.height);
const aspectRatio = computed(() => imageWidth.value / imageHeight.value);
const flexWidthBasis = computed(() => aspectRatio.value * basis.value);

// x3 is for high resolution displays
const imageWidthBasis = computed(() => Math.round(flexWidthBasis.value * 3));

const { isPhoto, isVideo, imageUrl, videoUrl } = usePhoto(
  photo.value,
  imageWidthBasis
);

const styleObj = computed(() => {
  return {
    // aspectRatio: aspectRatio.value,
    flex: `0 1 ${flexWidthBasis.value}px`,
    // flex: `${aspectRatio.value} 1 ${flexWidthBasis.value}px`,
  };
});

const label = computed(() =>
  [photo.value.title, photo.value.caption]
    .filter((item) => Boolean(item))
    .join(" - ")
);

const playVideo = () => video.value?.play().catch((_) => {});
const pauseVideo = () => video.value?.pause();

const showVideo = computed(() => isVideo.value && isVisible.value);
const shouldPlay = computed(
  () => isVideo.value && video.value && !dialog.value && isVisible.value
);

// const placeholderStyle = computed(() => ({
//   width: `${photo.value.width}px`,
//   height: `${photo.value.height}px`,
// }));

watch(shouldPlay, (shouldPlay) => {
  if (shouldPlay) {
    playVideo();
  } else {
    pauseVideo();
  }
});

onMounted(() => {
  if (shouldPlay.value) {
    playVideo();
  }
});
</script>

<style scoped lang="postcss">
.img-wrap {
  /* scroll-mt-32 prevents the #slug scroll to offset from top of screen */
  @apply cursor-pointer scroll-mt-32;
  > img {
    @apply rounded;
  }
}
</style>
